import { useQuery } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api";
import { currentPushSubscription, disablePush, enablePush, pushSupport } from "@/lib/pwa";
import { cn } from "@/lib/utils";

// «Push в браузере» (fd-web-04) в настройках уведомлений. В отличие от галочек
// тем, переключатель действует сразу: разрешение браузер спрашивает только по
// нажатию. Какие уведомления придут — решают те же темы, что и у ленты.
// Канал не настроен на шлюзе (нет VAPID) или браузер не умеет push — блока нет.
export function PushToggle() {
  const support = pushSupport();
  const config = useQuery({
    queryKey: ["push", "config"],
    queryFn: () => api.push.config(),
    staleTime: 60 * 60_000,
    enabled: support !== "unsupported"
  });
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const denied = support === "supported" && typeof Notification !== "undefined" && Notification.permission === "denied";
  useEffect(() => {
    if (support !== "supported") return;
    let alive = true;
    void currentPushSubscription().then(sub => alive && setOn(sub != null)).catch(() => alive && setOn(false));
    return () => {
      alive = false;
    };
  }, [support]);
  if (support === "unsupported" || !config.data?.enabled) return null;
  const toggle = async (next: boolean) => {
    setBusy(true);
    try {
      if (next) {
        const result = await enablePush(config.data?.publicKey ?? "");
        if (result === "denied") {
          toast.error("Браузер запретил уведомления. Разрешите их в настройках сайта.");
          setOn(false);
          return;
        }
        setOn(true);
        toast.success("Push-уведомления включены");
      } else {
        await disablePush();
        setOn(false);
        toast.success("Push-уведомления выключены");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось изменить push-уведомления");
    } finally {
      setBusy(false);
    }
  };
  const checked = on === true;
  return <div className="mt-4 border-t border-[#E2E2E2] pt-3">
			<label className={cn("flex items-start gap-2.5", support === "supported" && on !== null && !busy && !denied ? "cursor-pointer" : "cursor-default opacity-80")}>
				<input type="checkbox" checked={checked} disabled={support !== "supported" || on === null || busy || denied && !checked} onChange={e_0 => void toggle(e_0.target.checked)} className="peer sr-only" />
				<span aria-hidden="true" className={cn("mt-[1px] grid size-5 shrink-0 place-items-center rounded-[5px] border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[#1F1F1F]/40", checked ? "border-[#FF5E00] bg-[#FF5E00] text-white" : "border-[#1F1F1F]/24")}>
					{checked && <Check className="size-3.5" strokeWidth={2.5} />}
				</span>
				<span>
					<span className="block text-[16px] leading-[21px] font-medium text-[#1F1F1F]">Push в браузере</span>
					<span className="block text-[12px] leading-4 text-black/50">
						{support === "ios-needs-install" ? "На iPhone push работает, когда OSMIX добавлен на экран Домой" : denied ? "Уведомления запрещены в настройках браузера для этого сайта" : "Статусы заказов и ответы придут, даже когда вкладка закрыта"}
					</span>
				</span>
			</label>
		</div>;
}